import { useIntl } from "react-intl";

import Button from "@/components/wpl/button/src";
import {
    FeaturesTable,
    FeaturesTableHeader,
    FeaturesTableRow,
    FeaturesTableSection
} from "@/components/wpl/pricing/src";
import { useLocalizedHref } from "@/libraries/web-framework";

import AdminInsights from "../modal-contents/admin-insights";
import AdvancedAlertIntegrations from "../modal-contents/advanced-alert-integrations";
import AgentLimitPerSite from "../modal-contents/agent-limit-per-site";
import AlertNotifications from "../modal-contents/alert-notifications";
import AppsAndIntegrations from "../modal-contents/apps-and-integrations";
import AssetAndServiceRepository from "../modal-contents/asset-and-service-repository";
import AssetDiscovery from "../modal-contents/asset-discovery";
import AtlassianAnalytics from "../modal-contents/atlassian-analytics";
import AtlassianDataLake from "../modal-contents/atlassian-data-lake";
import AtlassianGuardPremium from "../modal-contents/atlassian-guard-premium";
import AtlassianGuardStandard from "../modal-contents/atlassian-guard-standard";
import AtlassianIntelligence from "../modal-contents/atlassian-intelligence";
import AuditLogs from "../modal-contents/audit-logs";
import Automation from "../modal-contents/automation";
import BusinessContinuity from "../modal-contents/business-continuity";
import ChangeCalendar from "../modal-contents/change-calendar";
import ChangeRiskAssessmentEngine from "../modal-contents/change-risk-assessment-engine";
import Chat from "../modal-contents/chat";
import ConfigurableWorkflows from "../modal-contents/configurable-workflows";
import CustomDomains from "../modal-contents/custom-domains";
import CustomerLimit from "../modal-contents/customer-limit";
import CustomerProfilesAndFields from "../modal-contents/customer-profiles-and-fields";
import CustomerServiceManagementTemplate from "../modal-contents/customer-service-management-template";
import CustomizableQueues from "../modal-contents/customizable-queues";
import DataConnectors from "../modal-contents/data-connectors";
import DataResidency from "../modal-contents/data-residency";
import DependencyMapping from "../modal-contents/dependency-mapping";
import DeploymentGating from "../modal-contents/deployment-gating";
import DeploymentTracking from "../modal-contents/deployment-tracking";
import DomainVerification from "../modal-contents/domain-verification";
import DynamicObjectFields from "../modal-contents/dynamic-object-fields";
import EmailAndWidgetSupport from "../modal-contents/email-and-widget-support";
import EmailNotifications from "../modal-contents/email-notifications";
import EmbeddedKnowledgeBase from "../modal-contents/embedded-knowledge-base";
import Encryption from "../modal-contents/encryption";
import EscalationsToDevelopmentTeams from "../modal-contents/escalations-to-development-teams";
import ExternalCustomerSSO from "../modal-contents/external-customer-sso";
import ExternalServices from "../modal-contents/external-services";
import FacilitiesServiceManagementTemplate from "../modal-contents/facilities-service-management-template";
import FeaturedArticles from "../modal-contents/featured-articles";
import Forms from "../modal-contents/forms";
import GeneralServiceManagementTemplate from "../modal-contents/general-service-management-template";
import HeartbeatMonitoring from "../modal-contents/heartbeat-monitoring";
import HrServiceManagementTemplate from "../modal-contents/hr-service-management-template";
import IncidentCreation from "../modal-contents/incident-creation";
import IncidentInvestigation from "../modal-contents/incident-investigation";
import InternalStakeholders from "../modal-contents/internal-stakeholders";
import IPAllowlisting from "../modal-contents/ip-allowlisting";
import ITSMProjectTemplate from "../modal-contents/itsm-project-template";
import KnowledgeBaseReports from "../modal-contents/knowledge-base-reports";
import LegalServiceManagementTemplate from "../modal-contents/legal-service-management-template";
import MajorIncidentEscalation from "../modal-contents/major-incident-escalation";
import MDM from "../modal-contents/mdm";
import MobileAppSupport from "../modal-contents/mobile-app-support";
import MonitoringAndChatOpsIntegrations from "../modal-contents/monitoring-and-chat-ops-integrations";
import MultiLanguageSupport from "../modal-contents/multi-language-support";
import MultiUserAndGroupApprovals from "../modal-contents/multi-user-and-group-approvals";
import MultipleIdP from "../modal-contents/multiple-idp";
import NumberOfSites from "../modal-contents/number-of-sites";
import OnCallManagement from "../modal-contents/on-call-management";
import OrganizationProfilesAndFields from "../modal-contents/organization-profiles-and-fields";
import PasswordPolicies from "../modal-contents/password-policies";
import PostIncidentReviews from "../modal-contents/post-incident-reviews";
import ProductRequests from "../modal-contents/product-requests";
import ReleaseTracks from "../modal-contents/release-tracks";
import ReportingAndAnalytics from "../modal-contents/reporting-and-analytics";
import Sandbox from "../modal-contents/sandbox";
import SelfServicePortal from "../modal-contents/self-service-portal";
import ServiceAndInfrastructureHealthAnalysis from "../modal-contents/service-and-infrastructure-health-analysis";
import ServiceDependency from "../modal-contents/service-dependency";
import ServiceRegistry from "../modal-contents/service-registry";
import ServiceStatusPages from "../modal-contents/service-status-pages";
import ServiceSubscriptions from "../modal-contents/service-subscriptions";
import SessionDurationManagement from "../modal-contents/session-duration-management";
import SLAManagement from "../modal-contents/sla-management";
import SmartArticleSuggestion from "../modal-contents/smart-article-suggestion";
import Storage from "../modal-contents/storage";
import Support from "../modal-contents/support";
import UserCounts from "../modal-contents/user-counts";
import VirtualAgent from "../modal-contents/virtual-agent";

const tiers = ["Free", "Standard", "Premium", "Enterprise"];

const CloudFeaturesTables = () => {
    const intl = useIntl();
    const buttonText = intl.formatMessage({
        id: "JiraServiceManagementPricingPage.features.learnAboutFeatures",
        defaultMessage: "Learn about features"
    });

    return (
        <FeaturesTableSection>
            <FeaturesTableHeader>
                {intl.formatMessage({
                    id: "JiraServiceManagementPricingPage.featuresTableHeader",
                    defaultMessage: "Compare features"
                })}
            </FeaturesTableHeader>
            <GeneralServiceManagement />
            <IncidentAndProblemManagement />
            <ServiceBasedOrganization />
            <ChangeManagement />
            <AssetAndServiceConfigurationManagement />
            <EnterpriseServiceManagement />
            <CustomerServiceManagement />
            <KnowledgeManagement />
            <DataAndInsights />
            <SecurityAndControls />
            <Button
                type="secondary"
                color="darkblue"
                text={buttonText}
                aria-label={buttonText}
                url={useLocalizedHref(
                    "/software/jira/service-management/features"
                )}
                dataEventProps={{
                    "data-event": "clicked",
                    "data-product-key": "jiraServiceManagement",
                    "data-event-component": "linkButton",
                    "data-event-container": "featuresSection"
                }}
            />
        </FeaturesTableSection>
    );
};

const GeneralServiceManagement = () => {
    const intl = useIntl();

    const messages = {
        generalServiceManagement: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.generalServiceManagement",
            defaultMessage: "General Service Management"
        }),
        agentLimit: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.agentLimitPerSite",
            defaultMessage: "Agent limit per site"
        }),
        agents3: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.agentLimit.3",
            defaultMessage: "3 agents"
        }),
        agents20k: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.agentLimit.20k",
            defaultMessage: "20,000 agents"
        }),
        customerLimit: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.customerLimit",
            defaultMessage: "Customer limit"
        }),
        unlimited: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.unlimited",
            defaultMessage: "Unlimited"
        }),
        numSites: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.numSites",
            defaultMessage: "Number of sites"
        }),
        numSitesOne: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.numSites.one",
            defaultMessage: "One"
        }),
        numSites150: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.numSites.150",
            defaultMessage: "Up to 150"
        }),
        support: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.support",
            defaultMessage: "Support"
        }),
        freeSupport: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.support.free",
            defaultMessage: "Atlassian Community"
        }),
        standardSupport: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.support.standard",
            defaultMessage: "Business hour support"
        }),
        premiumSupport: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.support.premium",
            defaultMessage: "24/7 support for critical issues"
        }),
        enterpriseSupport: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.support.enterprise",
            defaultMessage: "24/7 dedicated senior support for all issues"
        }),
        storage: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.storage",
            defaultMessage: "Storage"
        }),
        storate2GB: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.storage.free",
            defaultMessage: "2 GB file storage"
        }),
        storage250GB: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.storage.standard",
            defaultMessage: "250 GB file storage"
        }),
        storageUnlimited: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.storage.premium",
            defaultMessage: "Unlimited file storage"
        }),
        emailNotifications: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.emailNotifications",
            defaultMessage: "Email notifications"
        }),
        emails100: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.emailNotifications.free",
            defaultMessage: "100 emails per day"
        }),
        uptimeSLA: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.uptimeSLA",
            defaultMessage: "Uptime SLA"
        }),
        notIncluded: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.uptimeSLA.notIncluded",
            defaultMessage: "Not included"
        }),
        uptime999: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.uptimeSLA.standard",
            defaultMessage: "99.9%"
        }),
        uptime9995: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.uptimeSLA.premium",
            defaultMessage: "99.95%"
        }),
        automation: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.automation",
            defaultMessage: "Automation"
        }),
        ruleRuns500: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.automation.free",
            defaultMessage: "500 rule runs per month"
        }),
        ruleRuns5000: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.automation.standard",
            defaultMessage: "5000 rule runs per month"
        }),
        ruleRuns1000PerUser: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.automation.premium",
            defaultMessage: "1000 rule runs per month per user"
        }),
        emailAndWidgetSupport: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.emailAndWidgetSupport",
            defaultMessage: "Email and embeddable widget support"
        }),
        selfServicePortal: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.selfServicePortal",
            defaultMessage: "Self-service portal"
        }),
        forms: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.forms",
            defaultMessage: "Forms"
        }),
        configurableWorkflows: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.configurableWorkflows",
            defaultMessage: "Configurable workflows"
        }),
        customizableQueues: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.customizableQueues",
            defaultMessage: "Customizable queues"
        }),
        slaManagement: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.slaManagement",
            defaultMessage: "SLA (service-level agreement) management"
        }),
        reportingAndAnalytics: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.reportingAndAnalytics",
            defaultMessage: "Reporting and analytics"
        }),
        itsmProjectTemplate: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.itsmProjectTemplate",
            defaultMessage: "ITSM project template"
        }),
        multiLanguageSupport: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.multiLanguageSupport",
            defaultMessage: "Multi-language support"
        }),
        appsAndIntegrations: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.appsAndIntegrations",
            defaultMessage: "Apps and integrations"
        }),
        mobileAppSupport: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.mobileAppSupport",
            defaultMessage: "Mobile app support"
        }),
        chat: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.chat",
            defaultMessage: "Chat"
        }),
        customDomains: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.customDomains",
            defaultMessage: "Custom domains (Beta)"
        }),
        virtualAgent: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.virtualAgent",
            defaultMessage: "Virtual agent"
        }),
        atlassianIntelligence: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.atlassianIntelligence",
            defaultMessage: "Atlassian Intelligence (AI)"
        })
    };

    return (
        <FeaturesTable
            captionText={messages.generalServiceManagement}
            planTiers={tiers}
        >
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.agentLimit,
                    content: AgentLimitPerSite
                }}
                tierCellsContent={[
                    messages.agents3,
                    messages.agents20k,
                    messages.agents20k,
                    messages.agents20k
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.customerLimit,
                    content: CustomerLimit
                }}
                tierCellsContent={[
                    messages.unlimited,
                    messages.unlimited,
                    messages.unlimited,
                    messages.unlimited
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.numSites,
                    content: NumberOfSites
                }}
                tierCellsContent={[
                    messages.numSitesOne,
                    messages.numSitesOne,
                    messages.numSitesOne,
                    messages.numSites150
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.support,
                    content: Support
                }}
                tierCellsContent={[
                    messages.freeSupport,
                    messages.standardSupport,
                    messages.premiumSupport,
                    messages.enterpriseSupport
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.storage,
                    content: Storage
                }}
                tierCellsContent={[
                    messages.storate2GB,
                    messages.storage250GB,
                    messages.storageUnlimited,
                    messages.storageUnlimited
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.emailNotifications,
                    content: EmailNotifications
                }}
                tierCellsContent={[
                    messages.emails100,
                    messages.unlimited,
                    messages.unlimited,
                    messages.unlimited
                ]}
            />
            <FeaturesTableRow
                featureCellContent={messages.uptimeSLA}
                tierCellsContent={[
                    messages.notIncluded,
                    messages.notIncluded,
                    messages.uptime999,
                    messages.uptime9995
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.automation,
                    content: Automation
                }}
                tierCellsContent={[
                    messages.ruleRuns500,
                    messages.ruleRuns5000,
                    messages.ruleRuns1000PerUser,
                    messages.unlimited
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.emailAndWidgetSupport,
                    content: EmailAndWidgetSupport
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.selfServicePortal,
                    content: SelfServicePortal
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.forms,
                    content: Forms
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.configurableWorkflows,
                    content: ConfigurableWorkflows
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.customizableQueues,
                    content: CustomizableQueues
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.slaManagement,
                    content: SLAManagement
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.reportingAndAnalytics,
                    content: ReportingAndAnalytics
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.itsmProjectTemplate,
                    content: ITSMProjectTemplate
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.multiLanguageSupport,
                    content: MultiLanguageSupport
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.appsAndIntegrations,
                    content: AppsAndIntegrations
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.mobileAppSupport,
                    content: MobileAppSupport
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.chat,
                    content: Chat
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.customDomains,
                    content: CustomDomains
                }}
                tierCellsContent={[false, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.virtualAgent,
                    content: VirtualAgent
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.atlassianIntelligence,
                    content: AtlassianIntelligence
                }}
                tierCellsContent={[false, false, true, true]}
            />
        </FeaturesTable>
    );
};

const IncidentAndProblemManagement = () => {
    const intl = useIntl();

    const messages = {
        incidentAndProblemManagement: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.incidentAndProblemManagement",
            defaultMessage: "Incident and Problem management"
        }),
        alertNotifications: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.alertNotifications",
            defaultMessage: "Alert notifications per month"
        }),
        alertNotifications200SMS: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.alertNotifications.free",
            defaultMessage:
                "Unlimited alerts, unlimited alert email notifications, and 200 SMS"
        }),
        alertNotificationsSMS: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.alertNotifications.standard",
            defaultMessage:
                "Unlimited alerts, unlimited alert email notifications, and SMS"
        }),
        alertNotificationsVoice: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.alertNotifications.premium",
            defaultMessage:
                "Unlimited alerts, unlimited alert email notifications, SMS, and voice"
        }),
        incidentCreation: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.incidentCreation",
            defaultMessage: "Incident creation"
        }),
        manual: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.incidentCreation.manual",
            defaultMessage: "Manual"
        }),
        manualPlus: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.incidentCreation.manualPlus",
            defaultMessage: "Manual, automatic, and API"
        }),
        internalStakeholders: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.internalStakeholders",
            defaultMessage: "Internal stakeholders"
        }),
        monitoringAndChatOpsIntegrations: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.monitoringAndChatOpsIntegrations",
            defaultMessage: "Monitoring and ChatOps integrations"
        }),
        majorIncidentEscalation: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.majorIncidentEscalation",
            defaultMessage: "Major incident escalation"
        }),
        postIncidentReviews: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.postIncidentReviews",
            defaultMessage: "Post-incident reviews"
        }),
        onCallManagement: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.onCallManagement",
            defaultMessage: "On-call management"
        }),
        advancedAlertIntegrations: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.advancedAlertIntegrations",
            defaultMessage: "Advanced alert integrations"
        }),
        incidentInvestigation: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.incidentInvestigation",
            defaultMessage: "Incident investigation"
        }),
        heartbeatMonitoring: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.heartbeatMonitoring",
            defaultMessage: "Heartbeat monitoring"
        })
    };

    return (
        <FeaturesTable
            captionText={messages.incidentAndProblemManagement}
            planTiers={tiers}
        >
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.alertNotifications,
                    content: AlertNotifications
                }}
                tierCellsContent={[
                    messages.alertNotifications200SMS,
                    messages.alertNotificationsSMS,
                    messages.alertNotificationsVoice,
                    messages.alertNotificationsVoice
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.incidentCreation,
                    content: IncidentCreation
                }}
                tierCellsContent={[
                    messages.manual,
                    messages.manual,
                    messages.manualPlus,
                    messages.manualPlus
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.internalStakeholders,
                    content: InternalStakeholders
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.monitoringAndChatOpsIntegrations,
                    content: MonitoringAndChatOpsIntegrations
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.majorIncidentEscalation,
                    content: MajorIncidentEscalation
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.postIncidentReviews,
                    content: PostIncidentReviews
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.onCallManagement,
                    content: OnCallManagement
                }}
                tierCellsContent={[false, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.advancedAlertIntegrations,
                    content: AdvancedAlertIntegrations
                }}
                tierCellsContent={[false, false, true, true]}
            />

            <FeaturesTableRow
                featureCellContent={{
                    text: messages.incidentInvestigation,
                    content: IncidentInvestigation
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.heartbeatMonitoring,
                    content: HeartbeatMonitoring
                }}
                tierCellsContent={[false, false, true, true]}
            />
        </FeaturesTable>
    );
};

const ServiceBasedOrganization = () => {
    const intl = useIntl();

    const messages = {
        serviceBasedOrganization: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.serviceBasedOrganization",
            defaultMessage: "Service-based organization"
        }),
        serviceRegistry: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.serviceRegistry",
            defaultMessage: "Service Registry"
        }),
        serviceStatusPages: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.serviceStatusPages",
            defaultMessage: "Service status pages"
        }),
        serviceDependency: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.serviceDependency",
            defaultMessage: "Service dependency"
        }),
        serviceSubscriptions: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.serviceSubscriptions",
            defaultMessage: "Service subscriptions"
        }),
        externalServices: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.externalServices",
            defaultMessage: "External services"
        }),
        serviceAndInfrastructureHealthAnalysis: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.serviceAndInfrastructureHealthAnalysis",
            defaultMessage: "Service and infrastructure health analysis"
        })
    };

    return (
        <FeaturesTable
            captionText={messages.serviceBasedOrganization}
            planTiers={tiers}
        >
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.serviceRegistry,
                    content: ServiceRegistry
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.serviceStatusPages,
                    content: ServiceStatusPages
                }}
                tierCellsContent={[false, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.serviceDependency,
                    content: ServiceDependency
                }}
                tierCellsContent={[false, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.serviceSubscriptions,
                    content: ServiceSubscriptions
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.externalServices,
                    content: ExternalServices
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.serviceAndInfrastructureHealthAnalysis,
                    content: ServiceAndInfrastructureHealthAnalysis
                }}
                tierCellsContent={[false, false, true, true]}
            />
        </FeaturesTable>
    );
};

const ChangeManagement = () => {
    const intl = useIntl();

    const messages = {
        changeManagement: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.changeManagement",
            defaultMessage: "Change Management"
        }),
        multiUserAndGroupApprovals: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.multiUserAndGroupApprovals",
            defaultMessage: "Multi-user and group approvals"
        }),
        changeRiskAssessmentEngine: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.changeRiskAssessmentEngine",
            defaultMessage: "Change risk assessment engine"
        }),
        changeCalendar: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.changeCalendar",
            defaultMessage: "Change calendar"
        }),
        deploymentTracking: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.deploymentTracking",
            defaultMessage: "Deployment tracking"
        }),
        deploymentGating: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.deploymentGating",
            defaultMessage: "Deployment gating"
        })
    };

    return (
        <FeaturesTable
            captionText={messages.changeManagement}
            planTiers={tiers}
        >
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.multiUserAndGroupApprovals,
                    content: MultiUserAndGroupApprovals
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.changeRiskAssessmentEngine,
                    content: ChangeRiskAssessmentEngine
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.changeCalendar,
                    content: ChangeCalendar
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.deploymentTracking,
                    content: DeploymentTracking
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.deploymentGating,
                    content: DeploymentGating
                }}
                tierCellsContent={[false, false, true, true]}
            />
        </FeaturesTable>
    );
};

const AssetAndServiceConfigurationManagement = () => {
    const intl = useIntl();

    const messages = {
        assetAndServiceConfigurationManagement: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.assetAndServiceConfigurationManagement",
            defaultMessage: "Asset and Service Configuration Management"
        }),
        assetAndServiceRepository: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.assetAndServiceRepository",
            defaultMessage: "Asset and service repository"
        }),
        assetDiscovery: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.assetDiscovery",
            defaultMessage: "Asset discovery"
        }),
        dependencyMapping: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.dependencyMapping",
            defaultMessage: "Dependency mapping"
        }),
        dynamicObjectFields: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.dynamicObjectFields",
            defaultMessage: "Dynamic object fields"
        })
    };

    return (
        <FeaturesTable
            captionText={messages.assetAndServiceConfigurationManagement}
            planTiers={tiers}
        >
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.assetAndServiceRepository,
                    content: AssetAndServiceRepository
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.assetDiscovery,
                    content: AssetDiscovery
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.dependencyMapping,
                    content: DependencyMapping
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.dynamicObjectFields,
                    content: DynamicObjectFields
                }}
                tierCellsContent={[false, false, true, true]}
            />
        </FeaturesTable>
    );
};

const EnterpriseServiceManagement = () => {
    const intl = useIntl();

    const messages = {
        enterpriseServiceManagement: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.enterpriseServiceManagement",
            defaultMessage: "Enterprise Service Management"
        }),
        hrServiceManagementTemplate: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.hrServiceManagementTemplate",
            defaultMessage: "HR service management template"
        }),
        facilitiesServiceManagementTemplate: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.facilitiesServiceManagementTemplate",
            defaultMessage: "Facilities service management template"
        }),
        legalServiceManagementTemplate: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.legalServiceManagementTemplate",
            defaultMessage: "Legal service management template"
        }),
        generalServiceManagementTemplate: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.generalServiceManagementTemplate",
            defaultMessage: "General service management template"
        })
    };

    return (
        <FeaturesTable
            captionText={messages.enterpriseServiceManagement}
            planTiers={tiers}
        >
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.hrServiceManagementTemplate,
                    content: HrServiceManagementTemplate
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.facilitiesServiceManagementTemplate,
                    content: FacilitiesServiceManagementTemplate
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.legalServiceManagementTemplate,
                    content: LegalServiceManagementTemplate
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.generalServiceManagementTemplate,
                    content: GeneralServiceManagementTemplate
                }}
                tierCellsContent={[true, true, true, true]}
            />
        </FeaturesTable>
    );
};

const CustomerServiceManagement = () => {
    const intl = useIntl();

    const messages = {
        customerServiceManagement: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.customerServiceManagement",
            defaultMessage: "Customer Service Management"
        }),
        customerServiceManagementTemplate: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.customerServiceManagementTemplate",
            defaultMessage: "Customer service management template"
        }),
        customerProfilesAndFields: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.customerProfilesAndFields",
            defaultMessage: "Customer profiles & fields"
        }),
        organizationProfilesAndFields: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.organizationProfilesAndFields",
            defaultMessage: "Organization profiles & fields"
        }),
        escalationsToDevelopmentTeams: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.escalationsToDevelopmentTeams",
            defaultMessage: "Escalations to development teams"
        }),
        externalCustomerSSO: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.externalCustomerSSO",
            defaultMessage: "External customer SSO (single sign-on)"
        }),
        requiresAtlassianGuardSubscription: intl.formatMessage({
            id: "JiraServiceManagementPricingPageTeam24.features.requiresAtlassianGuardSubscription",
            defaultMessage: "Requires Atlassian Guard subscription"
        }),
        included: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.included",
            defaultMessage: "Included"
        })
    };

    return (
        <FeaturesTable
            captionText={messages.customerServiceManagement}
            planTiers={tiers}
        >
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.customerServiceManagementTemplate,
                    content: CustomerServiceManagementTemplate
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.customerProfilesAndFields,
                    content: CustomerProfilesAndFields
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.organizationProfilesAndFields,
                    content: OrganizationProfilesAndFields
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.escalationsToDevelopmentTeams,
                    content: EscalationsToDevelopmentTeams
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.externalCustomerSSO,
                    content: ExternalCustomerSSO
                }}
                tierCellsContent={[
                    messages.requiresAtlassianGuardSubscription,
                    messages.requiresAtlassianGuardSubscription,
                    messages.requiresAtlassianGuardSubscription,
                    messages.included
                ]}
            />
        </FeaturesTable>
    );
};

const KnowledgeManagement = () => {
    const intl = useIntl();

    const messages = {
        knowledgeManagement: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.knowledgeManagement",
            defaultMessage: "Knowledge Management"
        }),
        embeddedKnowledgeBase: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.embeddedKnowledgeBase",
            defaultMessage: "Embedded knowledge base"
        }),
        knowledgeBaseReports: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.knowledgeBaseReports",
            defaultMessage: "Knowledge base reports"
        }),
        smartArticleSuggestion: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.smartArticleSuggestion",
            defaultMessage: "Smart article suggestion"
        }),
        featuredArticles: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.featuredArticles",
            defaultMessage: "Featured articles"
        })
    };

    return (
        <FeaturesTable
            captionText={messages.knowledgeManagement}
            planTiers={tiers}
        >
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.embeddedKnowledgeBase,
                    content: EmbeddedKnowledgeBase
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.knowledgeBaseReports,
                    content: KnowledgeBaseReports
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.smartArticleSuggestion,
                    content: SmartArticleSuggestion
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.featuredArticles,
                    content: FeaturedArticles
                }}
                tierCellsContent={[true, true, true, true]}
            />
        </FeaturesTable>
    );
};

const DataAndInsights = () => {
    const intl = useIntl();

    const messages = {
        dataAndInsights: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.dataAndInsights",
            defaultMessage: "Data and Insights"
        }),
        atlassianAnalytics: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.atlassianAnalytics",
            defaultMessage: "Atlassian Analytics"
        }),
        atlassianDataLake: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.atlassianDataLake",
            defaultMessage: "Atlassian Data Lake"
        }),
        dataConnectors: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.dataConnectors",
            defaultMessage: "Data connectors"
        })
    };

    return (
        <FeaturesTable captionText={messages.dataAndInsights} planTiers={tiers}>
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.atlassianAnalytics,
                    content: AtlassianAnalytics
                }}
                tierCellsContent={[false, false, false, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.atlassianDataLake,
                    content: AtlassianDataLake
                }}
                tierCellsContent={[false, false, false, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.dataConnectors,
                    content: DataConnectors
                }}
                tierCellsContent={[false, false, false, true]}
            />
        </FeaturesTable>
    );
};

const SecurityAndControls = () => {
    const intl = useIntl();

    const messages = {
        securityAndControls: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.securityAndControls",
            defaultMessage: "Security and Controls"
        }),
        atlassianAccess: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.atlassianAccess",
            defaultMessage:
                "Atlassian Access (SSO, SCIM, Active Directory Sync)"
        }),
        atlassianGuardStandard: intl.formatMessage({
            id: "JiraServiceManagementPricingPageTeam24.features.atlassianGuardStandard.title",
            defaultMessage:
                "Atlassian Guard Standard (SSO, SCIM, mobile app management)"
        }),
        requiresAtlassianGuardStandardSubscription: intl.formatMessage({
            id: "JiraServiceManagementPricingPageTeam24.features.requiresAtlassianGuardStandardSubscription",
            defaultMessage: "Requires Atlassian Guard Standard subscription"
        }),
        atlassianGuardPremium: intl.formatMessage({
            id: "JiraServiceManagementPricingPageTeam24.features.atlassianGuardPremium.title",
            defaultMessage:
                "Atlassian Guard Premium (Data classification, threat detections, content scanning)"
        }),
        requiresAtlassianGuardPremiumSubscription: intl.formatMessage({
            id: "JiraServiceManagementPricingPageTeam24.features.requiresAtlassianGuardPremiumSubscription",
            defaultMessage: "Requires Atlassian Guard Premium subscription"
        }),
        included: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.included",
            defaultMessage: "Included"
        }),
        passwordPolicies: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.passwordPolicies",
            defaultMessage: "Password policies"
        }),
        domainVerification: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.domainVerification",
            defaultMessage: "Domain verification and account capture"
        }),
        sessionDurationManagement: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.sessionDurationManagement",
            defaultMessage: "Session duration management (desktop)"
        }),
        encryption: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.encryption",
            defaultMessage: "Encryption in transit and at rest"
        }),
        businessContinuity: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.businessContinuity",
            defaultMessage: "Business continuity and disaster recovery"
        }),
        mdm: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.mdm",
            defaultMessage: "MDM (Mobile Device Management)"
        }),
        auditLogs: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.auditLogs",
            defaultMessage: "Audit logs"
        }),
        dataResidency: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.dataResidency",
            defaultMessage: "Data residency"
        }),
        adminInsights: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.adminInsights",
            defaultMessage: "Admin insights"
        }),
        ipAllowlisting: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.ipAllowlisting",
            defaultMessage: "IP allowlisting"
        }),
        sandbox: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.sandbox",
            defaultMessage: "Sandbox"
        }),
        releaseTracks: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.releaseTracks",
            defaultMessage: "Release tracks"
        }),
        multipleIdP: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.multipleIdP",
            defaultMessage: "Multiple IdP"
        }),
        productRequests: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.productRequests",
            defaultMessage: "Product requests"
        }),
        userCounts: intl.formatMessage({
            id: "JiraServiceManagementPricingPage.features.userCounts",
            defaultMessage: "User counts"
        })
    };

    return (
        <FeaturesTable
            captionText={messages.securityAndControls}
            planTiers={tiers}
        >
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.atlassianGuardStandard,
                    content: AtlassianGuardStandard
                }}
                tierCellsContent={[
                    messages.requiresAtlassianGuardStandardSubscription,
                    messages.requiresAtlassianGuardStandardSubscription,
                    messages.requiresAtlassianGuardStandardSubscription,
                    messages.included
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.atlassianGuardPremium,
                    content: AtlassianGuardPremium
                }}
                tierCellsContent={[
                    messages.requiresAtlassianGuardPremiumSubscription,
                    messages.requiresAtlassianGuardPremiumSubscription,
                    messages.requiresAtlassianGuardPremiumSubscription,
                    messages.requiresAtlassianGuardPremiumSubscription
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.passwordPolicies,
                    content: PasswordPolicies
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.domainVerification,
                    content: DomainVerification
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.sessionDurationManagement,
                    content: SessionDurationManagement
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.encryption,
                    content: Encryption
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.businessContinuity,
                    content: BusinessContinuity
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.mdm,
                    content: MDM
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.auditLogs,
                    content: AuditLogs
                }}
                tierCellsContent={[false, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.dataResidency,
                    content: DataResidency
                }}
                tierCellsContent={[false, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.adminInsights,
                    content: AdminInsights
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.ipAllowlisting,
                    content: IPAllowlisting
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.sandbox,
                    content: Sandbox
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.releaseTracks,
                    content: ReleaseTracks
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.multipleIdP,
                    content: MultipleIdP
                }}
                tierCellsContent={[false, false, false, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.productRequests,
                    content: ProductRequests
                }}
                tierCellsContent={[false, false, false, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.userCounts,
                    content: UserCounts
                }}
                tierCellsContent={[false, false, false, true]}
            />
        </FeaturesTable>
    );
};

export default CloudFeaturesTables;
